// src/components/admin/batches/AssignTutorModal.tsx
import React, { useEffect, useState } from "react";
import { useTutorStore } from "src/store/tutorStore";
import { useAdminBatchStore, Batch } from "src/store/adminBatchStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "src/components/ui/Dialog";
import { Button } from "src/components/ui/Button";
import { Input } from "src/components/ui/Input";
import { Search, UserCheck, Star, ShieldCheck, Loader2 } from "lucide-react";
import { useToast } from "src/hooks/useToast";

interface AssignTutorModalProps {
  isOpen: boolean;
  onClose: () => void;
  batch: Batch | null;
}

export const AssignTutorModal: React.FC<AssignTutorModalProps> = ({
  isOpen,
  onClose,
  batch,
}) => {
  const { toast } = useToast();
  const { tutors, fetchTutors, isLoading: isLoadingTutors } = useTutorStore();
  const { assignTutorToBatch } = useAdminBatchStore();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTutorId, setSelectedTutorId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      void fetchTutors();
      setSelectedTutorId(batch?.default_tutor_id || "");
      setSearchTerm("");
    }
  }, [isOpen, fetchTutors, batch]);

  const filteredTutors = tutors.filter((tutor) => {
    const fullName = `${tutor.first_name || ""} ${tutor.last_name || ""}`.toLowerCase();
    const email = (tutor.email || "").toLowerCase();
    const query = searchTerm.toLowerCase();
    return fullName.includes(query) || email.includes(query);
  });

  const handleAssign = async () => {
    if (!batch || !selectedTutorId) return;

    setIsSubmitting(true);
    try {
      const success = await assignTutorToBatch(batch.id, selectedTutorId);
      if (success) {
        toast({
          title: "Tutor Assigned",
          description: "Instructor assigned to the batch successfully.",
          type: "success",
        });
        onClose();
      } else {
        toast({
          title: "Assignment Failed",
          description: "Unable to update the batch tutor. Please try again.",
          type: "error",
        });
      }
    } catch (error) {
      toast({
        title: "Assignment Error",
        description: error instanceof Error ? error.message : "An unexpected error occurred.",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden bg-white sm:rounded-xl">
        <DialogHeader className="p-5 bg-[#faf9fe] border-b border-slate-200">
          <div className="flex items-center gap-2 text-xs font-mono text-purple-700 font-semibold mb-1">
            <span>{batch ? `BAT-${batch.id.slice(0, 8).toUpperCase()}` : "Batch Assignment"}</span>
          </div>
          <DialogTitle className="text-lg font-bold text-slate-900 tracking-tight">
            Assign Instructor to Batch
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-600">
            Select an approved tutor to lead sessions for {batch?.student_name || "this student"}.
          </DialogDescription>
        </DialogHeader>

        <div className="p-5 space-y-4">
          {/* Search box */}
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search tutors by name or email..."
              className="pl-9 text-xs h-9"
            />
          </div>

          {/* Tutors List */}
          <div className="max-h-64 overflow-y-auto space-y-2 pr-1 divide-y divide-slate-100">
            {isLoadingTutors ? (
              <div className="py-8 flex flex-col items-center justify-center text-xs text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin text-purple-600 mb-2" />
                <span>Loading available tutors...</span>
              </div>
            ) : filteredTutors.length === 0 ? (
              <div className="py-8 text-center text-xs text-slate-500">
                No matching tutors found.
              </div>
            ) : (
              filteredTutors.map((tutor) => {
                const isSelected = selectedTutorId === tutor.id;
                const initials = `${tutor.first_name?.[0] || ""}${tutor.last_name?.[0] || ""}`.toUpperCase() || "TU";

                return (
                  <div
                    key={tutor.id}
                    onClick={() => setSelectedTutorId(tutor.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition flex items-center justify-between ${
                      isSelected
                        ? "border-purple-600 bg-purple-50/60 ring-1 ring-purple-600"
                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-purple-100 text-purple-700 font-bold flex items-center justify-center text-xs shadow-2xs">
                        {initials}
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-xs text-slate-900">
                            {tutor.first_name} {tutor.last_name}
                          </span>
                          {tutor.verification_status === "verified" && (
                            <ShieldCheck className="w-3.5 h-3.5 text-purple-600" />
                          )}
                        </div>
                        <div className="text-xs text-slate-500">{tutor.email}</div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {tutor.rating && (
                        <div className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                          <span>{tutor.rating.toFixed(1)}</span>
                        </div>
                      )}
                      {isSelected && (
                        <div className="w-5 h-5 rounded-full bg-purple-600 text-white flex items-center justify-center">
                          <UserCheck className="w-3 h-3" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <DialogFooter className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleAssign}
            disabled={!selectedTutorId || isSubmitting}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                Assigning...
              </>
            ) : (
              "Confirm Assignment"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
